import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { Edit3Icon, Icon } from '@vrplatform/icons';
import {
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import {
  showLoadingNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { Box, Tooltip } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  EllipsisMenuAuditEffectsItem,
  HiddenFeatureIndicator,
} from '../../components';
import { useJournalEntriesModal } from '../../modals';
import { RefreshTransactionsJournalEntries } from '../../views/deposits/list/DepositEllipsisMenuItems';
import type { ExpenseRow } from '../../views/expenses/list/expenses/useExpenseTableQuery';
import { useExpenseDetailDrawer } from './useExpenseDetailDrawer';
import { isOpeningBalanceTransaction } from '@vrplatform/embed';

export const ExpenseEllipsisMenuItems = ({
  expense,
  openDeleteModal,
  openPaymentStatusModal,
  openUnreconcileModal,
  isDrawer,
}: {
  expense: ExpenseRow;
  openDeleteModal: () => void;
  openPaymentStatusModal: () => void;
  openUnreconcileModal: () => void;
  isDrawer?: boolean;
}) => {
  const goto = useNavigate();
  const { open: openQuickDetail } = useExpenseDetailDrawer();
  const { open: openJournalEntriesModal } = useJournalEntriesModal();
  const invalidate = useInvalidateApiQueries(['expenses']);

  const { mutateAsync, isPending: loadingOpeningAdvance } = useApiMutation(
    'put',
    '/transactions/{id}'
  );

  const openingBalance = isOpeningBalanceTransaction(expense.uniqueRef);

  const duplicate = () => {
    goto(`/expenses/add`, {
      state: {
        expenseId: expense.id,
      },
    });
  };

  const isFullyLocked =
    expense.lock.payment === 'locked' && expense.lock.expense === 'locked';

  const toggleOpeningAdvance = () => {
    const notificationId = `expense-opening-balance-${expense.id}`;
    const nextIsOpeningAdvance = !expense.isOpeningBalance;

    showLoadingNotification({
      id: notificationId,
    });

    mutateAsync({
      params: {
        path: {
          id: expense.id,
        },
      },
      body: {
        isOpeningBalance: nextIsOpeningAdvance,
      },
    })
      .then(() => {
        updateSuccessNotification({
          id: notificationId,
          title: nextIsOpeningAdvance
            ? 'Opening balance enabled'
            : 'Opening balance disabled',
          message: nextIsOpeningAdvance
            ? 'This expense is now marked as opening balance.'
            : 'This expense is no longer marked as opening balance.',
        });
        invalidate();
      })
      .catch((error) => {
        showApiErrorNotification({
          id: notificationId,
          error,
          title: 'Failed to update opening balance',
          defaultMessage:
            'We failed to update the opening balance status. Please try again and contact support if the issue persists.',
        });
      });
  };

  return (
    <>
      {!isDrawer && (
        <>
          <EllipsisMenuItem
            onClick={() => openQuickDetail(expense.id)}
            customIcon={<Icon icon={'EyeIcon'} size={16} />}
          >
            Quick view
          </EllipsisMenuItem>
          <EllipsisMenuDivider />
        </>
      )}

      {expense.isReconciled && (
        <>
          <EllipsisMenuItem
            onClick={openUnreconcileModal}
            customIcon={<Icon icon={'CrossCircleIcon'} size={16} />}
          >
            Unmatch
          </EllipsisMenuItem>
          <EllipsisMenuDivider />
        </>
      )}

      <EllipsisMenuItem
        onClick={() => goto(`/expense/${expense.id}/edit`)}
        customIcon={<Edit3Icon size={16} />}
        disabled={!!openingBalance || isFullyLocked}
        tooltip={
          openingBalance
            ? 'This is an opening balance and cannot be edited through here'
            : (expense.lock?.message ?? undefined)
        }
      >
        Edit
      </EllipsisMenuItem>

      <EllipsisMenuItem icon="CopyIcon" onClick={duplicate}>
        Duplicate
      </EllipsisMenuItem>

      <EllipsisMenuItem
        onClick={openPaymentStatusModal}
        customIcon={<Icon icon="CircleDollarIcon" size={16} />}
      >
        Mark as {expense.payment.status === 'paid' ? 'unpaid' : 'paid'}
      </EllipsisMenuItem>

      <EllipsisMenuDivider />

      <EllipsisMenuItem
        customIcon={<Icon icon="ListUnorderedIcon" size={16} />}
        onClick={() =>
          openJournalEntriesModal({
            transactionIds: [expense.id],
          })
        }
      >
        Journal Entries
      </EllipsisMenuItem>

      <RefreshTransactionsJournalEntries id={expense.id} />

      <Tooltip
        label={
          openingBalance
            ? 'This is an opening balance and cannot be deleted through here'
            : 'Please unmatch the expense before deleting'
        }
        withArrow
        disabled={!expense.isReconciled}
      >
        <Box>
          <EllipsisMenuDangerItem
            onClick={openDeleteModal}
            disabled={expense.isReconciled || !!openingBalance}
          >
            Delete
          </EllipsisMenuDangerItem>
        </Box>
      </Tooltip>

      <HiddenFeatureIndicator permission="vrp-admin">
        <EllipsisMenuItem
          customIcon={<Icon icon="CircleDollarIcon" size={16} />}
          onClick={toggleOpeningAdvance}
          loading={loadingOpeningAdvance}
        >
          {expense.isOpeningBalance
            ? 'Disable as opening balance'
            : 'Use as opening balance'}
        </EllipsisMenuItem>

        <EllipsisMenuAuditEffectsItem entityId={expense.id} />
        <EllipsisMenuCopyItem value={expense.id} />
      </HiddenFeatureIndicator>
    </>
  );
};
